
/**
 * Main App component for the Autodata frontend.
 * 
 * Responsibilities:
 * - Application routing with Redux integration
 * - Error boundary handling for graceful error recovery
 * - Cross-tab/window coordination and session management
 * - Keyboard shortcuts (Ctrl+S for search focus, Ctrl+D for debug mode, F1/F7 for help, Home key)
 * - Responsive UI updates based on window resize and scroll events
 * - Tab lifecycle management (initialization, focus, beforeunload)
 * 
 * @author fabrice.miras@vitecsoftware.com
 * @date 2025-11-03
 */

import React, { useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom'; // React Router v6
import { useDispatch, useSelector } from 'react-redux'; // Redux hooks
import Layout from './app/components/Layout'; // Main layout component
import ErrorHandler from './app/pages/error/ErrorHandler'; // Custom error handler UI
import U from './felles/Utils'; // Utility functions
import getRoutes from './felles/______________getRoutes'; // Function to get app routes
import * as actions from './store/actions/index'; // Redux actions
import qs from 'qs'; // Query string parser
import { AppState } from './store/rootReducer'; // Root state type
import { TabCoordinationManager } from './felles/TabCoordinationManager'; // Tab coordination manager
import { NavigationSetter } from './felles/NavigationSetter'; // Navigation service initializer

/**
 * Error fallback component rendered when an error is caught by ErrorBoundary
 */
const ErrorFallback = ({ error, resetErrorBoundary }) => <ErrorHandler error={error} resetErrorBoundary={resetErrorBoundary} />;

/**
 * Main application component
 */
const App: React.FC = () => {
  const dispatch = useDispatch();
  
  // Parse transaction ID from URL query string (e.g., ?transid=NV03)
  const transId: string = qs.parse(window.location.search, { ignoreQueryPrefix: true }).transid as string;

  // Redux state selectors
  const _isLogged: number = useSelector((state) => (state as AppState).auth.isLogged);
  const isAutodataLoginInProgress: boolean = useSelector((state) => (state as AppState).auth.isAutodataLoginInProgress);
  const showHelpContent: boolean = useSelector((state) => (state as AppState).trans.showHelpContent);
  const tabId: string = useSelector((state) => (state as AppState).auth.timeStamp);
  const isDebugEnabled: boolean = useSelector((state) => (state as AppState).utils.isDebugEnabled);
  const loginStateUI: number = useSelector((state) => (state as AppState).auth.loginStateUI);

  // Tab coordination manager - persisted across renders using useRef
  const tabManagerRef = useRef<TabCoordinationManager | null>(null);

  /**
   * Set the current tab ID in Redux (required for API calls and tab coordination)
   */
  useEffect(() => {
    dispatch(actions.setTabId(tabId));
  }, [dispatch, tabId]);

  /**
   * Register keyboard event listener on mount and cleanup on unmount
   */
  useEffect(() => {
    window.addEventListener('keydown', handlePressedKey);
    return () => {
      window.removeEventListener('keydown', handlePressedKey);
    };
  }, [dispatch, tabId]);

  /**
   * Handles global keyboard shortcuts:
   * - Ctrl+S: Focus on transaction code search input
   * - Ctrl+D: Toggle debug mode and recording
   * - Home: Focus on search input
   * - F1/F7: Help functionality (placeholder)
   * 
   * @param event - Keyboard event
   */
  function handlePressedKey(event): void {
    const { key } = event;
    if (key === 's' || key === 'd' || key === 'Home' || key === 'F7' || key === 'F1') {
      switch (true) {
        case key === 'F7' || key === 'F1':
          break;
        case event.ctrlKey && !event.shiftKey && key === 's' && !showHelpContent:
          event.preventDefault();
          if (document.getElementById('code-select') !== document.activeElement)
            document.getElementById('code-select')?.focus();
          break;
        case event.ctrlKey && !event.shiftKey && (key === 'd' || key === 'D'):
          event.preventDefault();
          dispatch(actions.switchDebugMode());
          if (isDebugEnabled) {
            dispatch(actions.setRecordingState(0));
            dispatch(actions.updateRecordingLog([]));
          }
          break;
        case key === 'Home':
          event.preventDefault();
          dispatch(actions.setSearchInputFocused(true));
          break;
        default:
          break;
      }
    }
  }

  /**
   * Handles errors caught by ErrorBoundary and dispatches error details to Redux
   * This allows the error state to be accessed across the application
   * 
   * @param error - The caught error object
   * @param info - Component stack trace information
   */
  function errorHandler(error: Error, info: React.ErrorInfo) {
    if (error && error.name) {
      dispatch(actions.setError({ name: error.name, message: error.message }));
    }
    if (info && info.componentStack) {
      dispatch(actions.setErrorInfo(info.componentStack.toString()));
    }
  }

  /**
   * Initialize TabCoordinationManager on first render
   * Uses lazy initialization pattern to create the instance only once
   */
  if (!tabManagerRef.current) {
    tabManagerRef.current = new TabCoordinationManager({
      dispatch,
      tabId,
      isLogged: _isLogged,
      loginStateUI,
      transId,
      isAutodataLoginInProgress
    });
  }

  /**
   * Update tab manager configuration when auth/login state changes
   * Keeps the tab coordination in sync with current application state
   */
  useEffect(() => {
    if (tabManagerRef.current) {
      tabManagerRef.current.updateConfig({
        isLogged: _isLogged,
        loginStateUI,
        transId,
        isAutodataLoginInProgress
      });
    }
  }, [_isLogged, loginStateUI, transId, isAutodataLoginInProgress]);

  /**
   * Initialize tab coordination and register window focus handler
   * The focus handler updates tab state when user switches back to this tab
   */
  useEffect(() => {
    const tabManager = tabManagerRef.current;
    if (!tabManager) return;

    // Initialize tab coordination
    tabManager.initialize();

    // Handle window focus event
    const handleWindowFocus = () => {
      tabManager.handleWindowFocus();
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [tabId]);

  /**
   * Notify other tabs when this tab's login state changes
   * Ensures all tabs are aware of authentication status changes
   */
  useEffect(() => {
    if (tabManagerRef.current) {
      tabManagerRef.current.updateTabState(_isLogged, loginStateUI);
    }
  }, [_isLogged, loginStateUI]);

  /**
   * Main application lifecycle effect
   * Handles window events: beforeunload, resize, and scroll
   * Sets up initial UI state and manages cleanup
   */
  useEffect(() => {
    const tabManager = tabManagerRef.current;

    /**
     * Cleanup tab from coordination list before window closes
     * Notifies other tabs that this tab is closing
     */
    const handleBeforeUnload = (event) => {
      if (tabManager) {
        tabManager.handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    /**
     * Updates UI state based on window dimensions
     * - Updates screen size in Redux
     * - Calculates current breakpoint (mobile, tablet, desktop)
     * - Manages drawer state (auto-close on mobile, auto-open on desktop)
     * - Resets sandwich menu button state
     */
    function handleResize() {
      dispatch(actions.setScreenSize(U.getWindowDimensions()));
      dispatch(actions.setCurrentBreakPoint(U.CURRENT_BREAKPOINT(U.getWindowDimensions().width)));
      // Reset sandwich menu button state
      dispatch(actions.setSandwichButtonStateClicked(false));
      // Auto-manage drawer based on screen width (< 1200px = mobile/tablet)
      if (U.getWindowDimensions().width > 0 && U.getWindowDimensions().width < 1200) {
        dispatch(actions.setDrawerState(false));
      } else {
        dispatch(actions.setDrawerState(true));
      }
    }

    /**
     * Updates scroll position in Redux state
     * Used for UI elements that depend on scroll position
     */
    function handleScroll() {
      dispatch(actions.setScrollTop(document.body.scrollTop));
    }

    // Set initial UI state
    handleResize();
    handleScroll();

    // Register debounced event listeners to improve performance
    window.addEventListener(
      'resize',
      U.debounce(() => {
        handleResize();
      })
    );

    document.body.addEventListener(
      'scroll',
      U.debounce(() => {
        handleScroll();
      })
    );

    /**
     * Cleanup all event listeners on component unmount
     */
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener(
        'resize',
        U.debounce(() => {
          handleResize();
        })
      );
      window.removeEventListener(
        'scroll',
        U.debounce(() => {
          handleScroll();
        })
      );
    };
  }, [dispatch, _isLogged]);

  /**
   * Render the application
   * 
   * Structure:
   * - Root div with keyboard event handling (tabIndex=0 makes it focusable)
   * - ErrorBoundary wraps everything for graceful error handling
   * - ReduxRouter provides routing with Redux integration
   * - Layout component provides the main application shell (header, drawer, content)
   * - getRoutes() returns all application route definitions
   */
  return (
    <div tabIndex={0} onKeyDown={(e) => handlePressedKey(e)}>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={errorHandler}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <NavigationSetter />
          <Layout>{getRoutes()}</Layout>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
};

export default App;